import * as React from 'react'
import { useEffect, useState } from 'react'
import { find } from 'lodash'
import { connect } from 'react-redux'

import Operations from '../../../../operations'
import { sizes } from '@lmc/common/constants'
import { history } from '../../../../store'
import { BackButton, HeaderSection, LabelTextBox } from '@lmc/ui-kit'
import { Spinner } from '../../../../components/generic'
import { ConsentFormTemplate, RouterProps } from '@lmc/common/types'

import { withRouter } from '../../../../withRouter'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'

type ConsentTemplateViewProps = {
  fetchTemplate: (id: string) => Promise<void>
  initialLoading?: boolean
  router: RouterProps
  template: ConsentFormTemplate
  updateTemplate: (
    id: string,
    body: string,
    successMsg: string
  ) => ConsentFormTemplate
}

export const ConsentTemplateView = ({
  fetchTemplate,
  initialLoading = true,
  router,
  template,
  updateTemplate,
}: ConsentTemplateViewProps): React.ReactElement => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(initialLoading)

  useEffect(() => {
    if (template) setIsLoading(false)

    fetchTemplate(router.params.id)
      .then(() => setIsLoading(false))
      .catch(() => {
        setIsLoading(false)
      })
  }, [router.params.id])

  const translate = (key: string, variable?: any): string =>
    t(`components.labelTextBox.${key}`, variable)

  const consentTemplateTranslate = (key: string) =>
    t(`consent.consentTemplatesLibrary.${key}`)

  if (isLoading || !template) return <Spinner />

  const isMobile = !isBrowser && !isTablet

  return (
    <div style={styles.container}>
      <BackButton onClick={() => history.back()} />
      <HeaderSection
        onClick={history.back}
        style={styles.header}
        title={template.title}
      />
      <LabelTextBox
        isMobile={isMobile}
        localisation={{
          title: template.title,
          readOnly: translate('readOnly'),
          loseText: translate('loseText'),
          save: t('general.buttons.save'),
          cancel: t('general.buttons.cancel'),
        }}
        content={template.body}
        copyOfTemplates={[template]}
        id={template.id}
        setCopyOfTemplates={async ([template]: any) => {
          setIsLoading(true)
          const newTemplate = await updateTemplate(
            template.id,
            template.body,
            consentTemplateTranslate('updateTemplate')
          )
          setIsLoading(false)
          router.navigate(`/settings/consent-templates/${newTemplate.id}`, {
            replace: true,
          })
        }}
        state="display"
      />
    </div>
  )
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '48px 24px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-end',
    paddingBottom: 24,
    paddingTop: 24,
  },
}

const mapStateToProps = (state: any, props: any) => ({
  template: find(state.data['consent-templates'], {
    id: props.router.params.id,
  }),
})

const mapDispatchToProps = (dispatch: any) => ({
  fetchTemplate: async (id: number) => {
    await dispatch(Operations.loadListItem('consent-templates', id))
  },
  updateTemplate: (id: string, body: string, successMsg: string) =>
    Operations.updateListItem({
      listId: 'consent-templates',
      itemId: id,
      json: { body },
      successMsg,
    })(dispatch),
})

export default withRouter(
  connect<any, any, any>(
    mapStateToProps,
    mapDispatchToProps
  )(ConsentTemplateView)
)
